/**
 * The firm's own identity and white-label branding.
 *
 * There is no `/me` endpoint on the advisor API — the JWT carries only a firm
 * UUID, not its name or logo. `GET /api/advisor/firm` is where the portal learns
 * who it belongs to, so it can wear that firm's brand instead of ours.
 *
 * `can_manage_branding` is the server's word on whether THIS advisor may change
 * the logo or accent (the firm's primary/admin). The UI gates on this, not on a
 * client-side guess — the same value the write endpoints enforce.
 */
import type { ApiClient } from '../apiClient';

export interface FirmBranding {
  id: string;
  name: string;
  /** Absolute path to the firm's logo (already cache-busted), or null if none set. */
  logo_url: string | null;
  /** A validated `#RRGGBB` accent, or null to use the house champagne-gold. */
  brand_accent: string | null;
  /** Whether the calling advisor is allowed to change the logo/accent. */
  can_manage_branding: boolean;
}

export function getFirm(api: ApiClient): Promise<FirmBranding> {
  return api.get<FirmBranding>('/api/advisor/firm');
}

export function uploadFirmLogo(
  api: ApiClient,
  input: { content_type: string; data_base64: string },
): Promise<{ logo_url: string }> {
  return api.post<{ logo_url: string }>('/api/advisor/firm/logo', input);
}

export function clearFirmLogo(api: ApiClient): Promise<{ ok: true }> {
  return api.del<{ ok: true }>('/api/advisor/firm/logo');
}

export function setFirmAccent(
  api: ApiClient,
  brand_accent: string | null,
): Promise<{ brand_accent: string | null }> {
  return api.put<{ brand_accent: string | null }>('/api/advisor/firm/branding', { brand_accent });
}

/** What the upload endpoint accepts. SVG is refused server-side (inline-SVG XSS). */
export const ACCEPTED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
export const MAX_LOGO_BYTES = 1024 * 1024; // 1 MiB — matches the server ceiling.

/**
 * Read a picked file into the `{ content_type, data_base64 }` the endpoint wants,
 * validating type and size HERE too so the person gets an instant, kind error
 * instead of a round-trip 400. The server re-checks everything (including the
 * bytes' magic number) — this is courtesy, not the security boundary.
 */
export function fileToLogoUpload(
  file: File,
): Promise<{ content_type: string; data_base64: string }> {
  return new Promise((resolve, reject) => {
    if (!ACCEPTED_LOGO_TYPES.includes(file.type as (typeof ACCEPTED_LOGO_TYPES)[number])) {
      reject(new Error('Please choose a PNG, JPEG, or WebP image. (SVG is not accepted.)'));
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      reject(new Error('That image is over 1 MB. Please choose a smaller file.'));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('We could not read that file.'));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('We could not read that file.'));
        return;
      }
      // FileReader gives `data:<type>;base64,<payload>` — strip to the payload.
      const comma = result.indexOf(',');
      resolve({ content_type: file.type, data_base64: comma >= 0 ? result.slice(comma + 1) : result });
    };
    reader.readAsDataURL(file);
  });
}
